/**
 * 票据刷新管理器
 * 对标 tv-linux 项目 src/util/util.js 的刷票逻辑
 *
 * 功能：
 * 1. 登录成功后自动计算刷新时间
 * 2. 定时检查票据有效期，在有效期 80% 时自动刷新
 * 3. 页面打开后自动初始化刷新定时器
 * 4. 票据刷新失败后的重试机制
 */

import { renew } from '@/services/authApi';
import { getCookieAmend } from '@/utils/apiUtils';

/**
 * 票据刷新管理器类
 */
class TokenRefreshManager {
  private static instance: TokenRefreshManager;

  /**
   * 获取单例实例
   */
  public static getInstance(): TokenRefreshManager {
    if (!TokenRefreshManager.instance) {
      TokenRefreshManager.instance = new TokenRefreshManager();
    }
    return TokenRefreshManager.instance;
  }

  private refreshTimer: NodeJS.Timeout | null = null;

  private isRefreshing = false;

  private retryCount = 0;

  private readonly MAX_RETRY_COUNT = 3;

  /**
   * 票据失效回调（401 时调用，由业务方决定交互流程）
   */
  private onTokenExpiredCallback?: () => void;

  private constructor() {
    // 私有构造函数，确保单例
  }

  /**
   * 设置票据失效回调
   * @param callback 票据失效时的回调函数
   */
  public setOnTokenExpiredCallback(callback: () => void): void {
    this.onTokenExpiredCallback = callback;
  }

  /**
   * 设置下一次刷新票据时间
   * 对标 tv-linux 的 setNextRefreshTime 函数
   *
   * @param customDuration 自定义刷新间隔（秒），不传则从 Cookie 中读取 next_refresh_token
   */
  public setNextRefreshTime(customDuration?: number): void {
    // 优先使用传入的时间间隔，其次使用后台返回的 next_refresh_token，最后使用默认值
    const nextDuration = customDuration || Number(getCookieAmend('next_refresh_token')) || 3600; // 单位为秒，默认 1 小时

    const now = new Date().getTime(); // 获取当前时间时间戳
    const nextFreshDate = new Date(now + nextDuration * 1000); // 计算出下次需要刷新登陆态日期
    localStorage.setItem('nextFreshDate', nextFreshDate.toUTCString()); // 缓存刷新时间
  }

  /**
   * 刷新票据
   * 对标 tv-linux 的 refreshLogin 函数
   */
  public async refreshLogin(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    try {
      // 调用票据续期接口
      const res = await renew();

      if (!res?.code) {
        // 刷新成功
        // 设置下次登录态刷新时间
        this.setNextRefreshTime();

        // 启动延时刷新登陆态
        this.createRefreshTimer();

        // 重置重试次数
        this.retryCount = 0;
      } else if (res?.code === 401) {
        // 登陆态短票据失效为 401 场景
        // 清除定时器
        this.clearRefreshTimer();

        // 调用业务方传入的回调，由业务方决定交互流程
        if (this.onTokenExpiredCallback) {
          this.onTokenExpiredCallback();
        }
      } else {
        // 其他错误
        // 重试机制
        if (this.retryCount < this.MAX_RETRY_COUNT) {
          this.retryCount += 1;

          // 30 秒后重试
          setTimeout(() => {
            this.refreshLogin();
          }, 30000);
        } else {
          this.clearRefreshTimer();
        }
      }
    } catch {
      // 重试机制
      if (this.retryCount < this.MAX_RETRY_COUNT) {
        this.retryCount += 1;

        // 30 秒后重试
        setTimeout(() => {
          this.refreshLogin();
        }, 30000);
      } else {
        this.clearRefreshTimer();
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * 创建登录态刷新定时器
   * 对标 tv-linux 的 createRefreshTimer 函数
   *
   * 优化策略：
   * 1. 如果没有缓存刷新时间，立即刷新
   * 2. 如果当前时间已经超过缓存的刷新时间（用户退出太久），立即刷新
   * 3. 如果当前时间超过有效期的 80%，立即刷新
   * 4. 否则，计算剩余时间并设置定时器
   */
  public createRefreshTimer(): void {
    const nowStamp = new Date().getTime(); // 当前时间时间戳

    const cachedNextFreshDate = localStorage.getItem('nextFreshDate');
    if (!cachedNextFreshDate) {
      // 没有 nextFreshDate 缓存刷新时间的情况下，立马启动刷新
      this.refreshLogin();
      return;
    }

    const localNextFreshStamp = new Date(cachedNextFreshDate).getTime();

    // 优化：如果当前时间已经超过了缓存的刷新时间（用户退出太久），立即刷新
    if (nowStamp >= localNextFreshStamp) {
      this.refreshLogin();
      return;
    }

    const efficientDate = (localNextFreshStamp - nowStamp) * 0.8 + nowStamp; // 有效期 80% 的时间

    if (nowStamp > efficientDate) {
      // 当前时间大于缓存的刷新时间戳 80%，立马启动刷新
      this.refreshLogin();
      return;
    }

    const nextDuration = parseInt(String(Number(localNextFreshStamp - nowStamp) * 0.8), 10);

    if (typeof nextDuration === 'number' && nextDuration > 0) {
      // 清除旧的定时器
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }

      // 创建新的定时器
      this.refreshTimer = setTimeout(() => {
        this.refreshLogin();
      }, nextDuration);
    }
  }

  /**
   * 短票据刷新
   * 对标 tv-linux 的 shortStiffExpired 函数
   */
  public shortStiffExpired(vusession?: string): void {
    if (vusession) {
      // 登陆态没有失效，启动延时刷新登陆态
      this.createRefreshTimer();
    } else {
      // 登陆态失效立即刷新登陆态
      this.refreshLogin();
    }
  }

  /**
   * 每次请求前都要实时检测用户登陆票据状态
   * 对标 tv-linux 的 beforeRequest 函数
   */
  public beforeRequest(): void {
    if (getCookieAmend('isLogin') === 'true') {
      const vusession = getCookieAmend('vusession'); // 登陆短票据

      // 短票据过期处理
      this.shortStiffExpired(vusession);
    }
  }

  /**
   * 清除刷新定时器
   */
  public clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * 初始化刷新管理器
   * 页面加载时调用，检查是否需要启动刷新定时器
   * @param onTokenExpired 票据失效回调（401 时调用）
   */
  public init(onTokenExpired?: () => void): void {
    // 设置票据失效回调
    if (onTokenExpired) {
      this.setOnTokenExpiredCallback(onTokenExpired);
    }

    // 检查是否已登录
    if (getCookieAmend('isLogin') === 'true') {
      const vusession = getCookieAmend('vusession');
      this.shortStiffExpired(vusession);
    }
  }

  /**
   * 销毁管理器（页面卸载时调用）
   */
  public destroy(): void {
    this.clearRefreshTimer();
    this.isRefreshing = false;
    this.retryCount = 0;
  }
}

/**
 * 导出单例实例
 */
export const tokenRefreshManager = TokenRefreshManager.getInstance();

/**
 * 便捷方法：初始化票据刷新
 * @param onTokenExpired 票据失效回调（401 时调用）
 */
export function initTokenRefresh(onTokenExpired?: () => void): void {
  tokenRefreshManager.init(onTokenExpired);
}

/**
 * 便捷方法：清除票据刷新定时器
 */
export function clearTokenRefresh(): void {
  tokenRefreshManager.destroy();
}

/**
 * 便捷方法：手动触发票据刷新
 */
export function manualRefreshToken(): void {
  tokenRefreshManager.refreshLogin();
}
